import { Globe } from 'lucide-react'

import { navigateTo, patternRoute, simpleRoute } from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import { UrlLinkBrand, UrlLinkSelect } from '../../server/database/types.js'
import { MenuItem, View } from '../types.js'
import { handleError } from '../utils/error-utils.js'
import {
    loadUrlAccessLogsIfNeeded,
    loadUrlLinkDetailIfNeeded,
    loadUrlLinksIfNeeded,
    reloadUrlLinks,
} from '../utils/load-utils.js'
import { UrlShortenerDetailView } from '../views/url-shortener/url-shortener-detail-view.js'
import { UrlShortenerView } from '../views/url-shortener/url-shortener-view.js'

const BRAND_DOMAINS: Record<UrlLinkBrand, string> = {
    play: 'url.play.ee',
    nope: 'url.nope.ee',
}

const getShortUrl = (link: UrlLinkSelect): string => {
    const domain = BRAND_DOMAINS[link.brand]
    return `${domain}/${link.short_code}`
}

const getQrUrl = (link: UrlLinkSelect): string =>
    `/api/url-links/${link.id}/qr?v=${encodeURIComponent(link.updated_at_utc)}`

class UrlShortenerModel {
    public route = simpleRoute<View>('/url-shortener', {
        onEnter: (view) => {
            loadUrlLinksIfNeeded(view)
        },
        render: (view) => {
            const { lang } = view.state
            const linksState = loadUrlLinksIfNeeded(view)
            const shortenerState = view.state.urlShortener

            return (
                <UrlShortenerView
                    lang={lang}
                    links={linksState.data ?? []}
                    isLoading={linksState.isLoading}
                    showCreateForm={shortenerState.showCreateForm}
                    isCreating={shortenerState.isCreating}
                    onToggleCreateForm={() => {
                        shortenerState.showCreateForm = !shortenerState.showCreateForm
                        view.update()
                    }}
                    onCreate={async (data) => {
                        shortenerState.isCreating = true
                        view.update()
                        try {
                            await view.api.urlShortener.createLink(data)
                            shortenerState.showCreateForm = false
                            reloadUrlLinks(view)
                        } catch (err) {
                            handleError(view, err)
                        } finally {
                            shortenerState.isCreating = false
                            view.update()
                        }
                    }}
                    onDelete={async (id) => {
                        shortenerState.isDeleting = true
                        view.update()
                        try {
                            await view.api.urlShortener.deleteLink(id)
                            reloadUrlLinks(view)
                        } catch (err) {
                            handleError(view, err)
                        } finally {
                            shortenerState.isDeleting = false
                            view.update()
                        }
                    }}
                    onViewDetail={(id) => {
                        navigateTo(`/url-shortener/${id}`)
                    }}
                    onCopyLink={(shortUrl) => {
                        void navigator.clipboard.writeText(shortUrl)
                    }}
                    getShortUrl={getShortUrl}
                />
            )
        },
    })

    public detailRoute = patternRoute<View, '/url-shortener/:id'>('/url-shortener/:id', {
        onEnter: (view, params) => {
            const id = Number(params.id)
            if (id) {
                loadUrlLinkDetailIfNeeded(view, id)
                loadUrlAccessLogsIfNeeded(view, id)
            }
        },
        render: (view, params) => {
            const { lang } = view.state
            const id = Number(params.id)
            const shortenerState = view.state.urlShortener

            const detailState = loadUrlLinkDetailIfNeeded(view, id)
            const logsState = loadUrlAccessLogsIfNeeded(view, id)

            const link = detailState.data
            const logsData = logsState.data
            const isNotFound = !detailState.isLoading && detailState.failCount > 0 && !link

            return (
                <UrlShortenerDetailView
                    lang={lang}
                    link={link}
                    isLoading={detailState.isLoading}
                    isNotFound={isNotFound}
                    isSavingQr={shortenerState.isSavingQr}
                    logs={logsData?.data ?? []}
                    logsTotal={logsData?.pagination.total ?? 0}
                    _logsPage={shortenerState.accessLogsPage}
                    logsLoading={logsState.isLoading}
                    stats={logsData?.stats}
                    qrSvgUrl={link ? getQrUrl(link) : ''}
                    onBack={() => navigateTo('/url-shortener')}
                    onSaveQr={async (settings) => {
                        if (!link) return
                        shortenerState.isSavingQr = true
                        view.update()
                        try {
                            const res = await view.api.urlShortener.saveQrSettings(
                                link.id,
                                settings,
                            )
                            detailState.data = res.data
                        } catch (err) {
                            handleError(view, err)
                        } finally {
                            shortenerState.isSavingQr = false
                            view.update()
                        }
                    }}
                    onDownloadQr={() => {
                        if (!link) return
                        const a = document.createElement('a')
                        a.href = getQrUrl(link)
                        a.download = `qr-${link.short_code}.svg`
                        a.click()
                    }}
                    onLoadMoreLogs={() => {
                        if (!link) return
                        shortenerState.accessLogsPage += 1
                        logsState.data = undefined
                        logsState.isLoading = false
                        loadUrlAccessLogsIfNeeded(view, link.id)
                    }}
                />
            )
        },
    })

    public menuItem: MenuItem = {
        text: t.urlShortener.menuLabel,
        icon: Globe,
        url: this.route.toUrl(),
        matchingRoutes: [this.route, this.detailRoute],
    }
}

export const urlShortenerModel = new UrlShortenerModel()
